import { useEffect, useState } from 'react'
import { Card } from './ui'
import {
  THEMES,
  type ModeId,
  type ThemeId,
  getMode,
  getTheme,
  setMode as persistMode,
  setTheme as persistTheme,
  watchSystemMode,
} from '../lib/theme'

const MODES: { id: ModeId; label: string; note: string }[] = [
  { id: 'dark', label: 'Dark', note: 'For a wall display or a long shift.' },
  { id: 'light', label: 'Light', note: 'For a bright room or a printout.' },
  { id: 'system', label: 'System', note: 'Follows the device setting.' },
]

/**
 * Theme picker. Lives in Settings and is per-browser, not per-account: the right theme depends on
 * the room the screen is in, and the same operator on a phone outdoors and a wall display at night
 * wants different answers. Storing it server-side would force one choice on both.
 */
export default function Appearance() {
  const [theme, setThemeState] = useState<ThemeId>(getTheme)
  const [mode, setModeState] = useState<ModeId>(getMode)

  useEffect(() => watchSystemMode(), [])

  const chooseTheme = (id: ThemeId) => {
    persistTheme(id)
    setThemeState(id)
  }

  const chooseMode = (id: ModeId) => {
    persistMode(id)
    setModeState(id)
  }

  return (
    <Card title="Appearance">
      <div className="stack" style={{ gap: 18 }}>
        <div>
          <div className="field-label">Mode</div>
          <div className="seg">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={mode === m.id ? 'seg-item active' : 'seg-item'}
                onClick={() => chooseMode(m.id)}
                aria-pressed={mode === m.id}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="field-hint">{MODES.find((m) => m.id === mode)?.note}</div>
        </div>

        <div>
          <div className="field-label">Theme</div>
          <div className="theme-grid">
            {THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={theme === t.id ? 'theme-swatch active' : 'theme-swatch'}
                onClick={() => chooseTheme(t.id)}
                aria-pressed={theme === t.id}
                title={t.note}
              >
                {/*
                  The preview carries its own data-theme, so each tile renders in the palette it
                  offers rather than in the one currently applied. That is the whole point of a
                  swatch — a list of names tells you nothing about what you are choosing.
                */}
                <span className="theme-preview" data-theme={t.id} data-mode={mode === 'system' ? undefined : mode} aria-hidden="true">
                  <span className="theme-preview-bar" />
                  <span className="theme-preview-dot" />
                </span>
                <span className="theme-name">{t.name}</span>
              </button>
            ))}
          </div>
          <div className="field-hint">
            {THEMES.find((t) => t.id === theme)?.note} Status colours — ok, warning, danger — stay the same
            across every theme on purpose: colour means state here, not decoration.
          </div>
        </div>
      </div>
    </Card>
  )
}
